/**
 * Run-Modus-UI (SCOPE_STAGE2 Punkt 10, docs/10): Run-Screen mit HUD + Shop,
 * Board-Editor (Stufe-1-Renderer), Fokus-Pick-, Ergebnis- und Run-Ende-Screen.
 * Jede Zustands-Mutation laeuft ueber src/game/ und wird sofort gespeichert (A8).
 */
import type { CombatResult } from '../sim/types'
import { Renderer, type EditView } from '../render/renderer'
import { RARITY_COLORS, SPECIES_EMOJI } from './theme'
import type { RunState, FocusId } from '../game/types'
import type { RunActions } from '../game/actions'
import { isPveRound, isRecurringPve } from '../game/runState'
import { canChooseItemPath, currentRerollCost } from '../game/shop'
import { deployCap, isLowHp, repairCost, sellValue, storageSize } from '../game/economy'
import { benchChampions, boardChampions, championByUid, storageWeapons } from '../game/roster'
import { findMerges } from '../game/merge'

const FOCUS_INFO: Record<FocusId, { title: string; desc: string; icon: string }> = {
  merge: { title: 'Merge-Fokus', desc: 'Erhöhte Zieh-Gewichtung für bereits besessene Champions', icon: '🔁' },
  variety: { title: 'Vielfalt-Fokus', desc: 'Erhöhte Gewichtung für noch nicht besessene Champions', icon: '✨' },
  rarityChamps: { title: 'Seltenheit (Champions)', desc: 'Höhere Chance auf höherwertige Champions', icon: '👑' },
  rarityItems: { title: 'Seltenheit (Items)', desc: 'Höhere Chance auf höherwertige Items/Waffen', icon: '💎' },
  gold: { title: 'Gold-Fokus', desc: 'Direkter Gold-Bonus statt RNG-Manipulation', icon: '🪙' },
}



export interface RunUiOptions {
  state: RunState
  /** alle Mutationen laufen ueber RunActions (Local ODER SpacetimeDB, docs/09 §7) */
  actions: RunActions
  onExit: () => void
  /** STDB: Rerender-Callback registrieren fuer Server-Drift/Fremd-Tab-Updates */
  subscribeExternal?: (cb: () => void) => () => void
}

export function mountRun(app: HTMLElement, opts: RunUiOptions): () => void {
  const { state, actions } = opts

  // TFT-Layout (BATTLE-21): Board immer sichtbar oben, Bench-Reihe darunter, Shop-Leiste unten.
  app.innerHTML = `
    <header id="rHud">
      <button id="rPause" title="Run pausieren — Fortschritt ist gespeichert">⏸ Pause</button>
      <h1>⚔️ <span id="rRound"></span></h1>
      <div class="row hud-hp"><label>HP</label><div class="hpbar"><div id="rHpFill"></div><span id="rHpText"></span></div></div>
      <div class="row"><span class="hud-stat" id="rGold"></span></div>
      <div class="row"><span class="hud-stat" id="rCap" title="Board-Belegung / Deploy-Cap"></span></div>
      <div class="row"><span class="hud-stat" id="rFractures" title="Fractures blocken Deployment-Felder"></span></div>
      <div class="row"><span class="hud-stat" id="rFocus"></span></div>
      <div class="row" style="margin-left:auto">
        <button id="rFight" class="primary big">⚔️ Kampf starten</button>
      </div>
    </header>
    <div class="run-board">
      <canvas id="rCanvas"></canvas>
      <div id="rOverlay" class="overlay hidden"></div>
      <div class="tickbar hidden" id="rTickbar">
        <button id="rPlayPause">⏸</button>
        <button id="rSpeed1" class="active">1×</button>
        <button id="rSpeed2">2×</button>
        <button id="rSpeed4">4×</button>
        <button id="rSkip">⏭ Ende</button>
        <span class="time" id="rTime"></span>
      </div>
      <div id="rUnitCard" class="unit-float" style="display:none">
        <div class="unit-float-head"><h3 id="rUnitTitle"></h3><button id="rUnitClose" title="Schließen">✕</button></div>
        <div id="rUnitBody"></div>
      </div>
    </div>
    <div class="run-shop" id="rShopStrip">
      <div id="rShopPanel" class="shop-panel"></div>
    </div>
    <div class="run-bench">
      <div class="bench-block">
        <div class="bench-cap">Bench <span id="rBenchCount" class="hint"></span></div>
        <div id="rBench" class="bench-row"></div>
      </div>
      <div class="bench-block storage-block">
        <div class="bench-cap">Items <span id="rStorageCount" class="hint"></span></div>
        <div id="rStorage" class="storage-row"></div>
      </div>
      <div class="bench-block merge-block" id="rMergeCard" style="display:none">
        <div class="bench-cap">Merges</div>
        <div id="rMerges" class="merge-row"></div>
      </div>
    </div>
  `

  const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T
  const canvas = $('rCanvas') as unknown as HTMLCanvasElement
  const renderer = new Renderer(canvas, { cols: state.config.grid.cols, rows: state.config.grid.rowsPerSide * 2 })
  renderer.attachCameraControls(canvas) // Rad = Zoom, Mitteltaste/Space+Drag = Pan, 0 = Reset (BATTLE-17)

  // ---------- ui state ----------
  let selectedUid: number | null = null
  let placingUid: number | null = null
  let hover: { col: number; row: number } | null = null
  let battle: {
    result: CombatResult
    simTime: number
    playing: boolean
    speed: number
    finished: boolean
  } | null = null
  let running = true

  const save = (): void => actions.persist()
  // Roster darf NUR in der Aufstell-Phase bearbeitet werden — nie während Kampf/Ergebnis
  // (sonst Verkaufen/Auf-die-Bench mitten im laufenden Kampf, BATTLE-21-Bugfix).
  const canEdit = (): boolean => battle === null && state.phase === 'shop'

  // ---------- HUD ----------
  function renderHud(): void {
    const cfg = state.config
    const pve = isPveRound(state.round, cfg)
    $('rRound').textContent = `Runde ${state.round} · ${pve ? 'PvE' : 'PvP'}${state.phase === 'shop' && !pve ? ` · Versuch ${state.attempt}` : ''}`
    const hpPct = Math.max(0, Math.min(100, (state.hp * 100) / cfg.hp.start))
    const fill = $('rHpFill')
    fill.style.width = `${hpPct}%`
    fill.style.background = isLowHp(state) ? '#e8564d' : hpPct < 60 ? '#d9a441' : '#4dd06a'
    $('rHpText').textContent = `${state.hp}/${cfg.hp.start}${isLowHp(state) ? ' · LOW HP' : ''}`
    $('rGold').textContent = `🪙 ${state.gold}`
    $('rFractures').textContent = `⚠ ${state.fractures.length}`
    $('rFocus').textContent = state.focus ? `${FOCUS_INFO[state.focus].icon} ${FOCUS_INFO[state.focus].title}` : ''
    $('rCap').textContent = `${boardChampions(state).length}/${deployCap(state.round, cfg)}`
    ;($('rFight') as HTMLButtonElement).disabled =
      state.phase !== 'shop' || boardChampions(state).length === 0 || battle !== null
  }

  // ---------- Shop ----------
  function renderShop(): void {
    const panel = $('rShopPanel')
    const cfg = state.config
    // Shop-Leiste nur in der Aufstell-Phase sichtbar (im Kampf mehr Platz fürs Board)
    $('rShopStrip').style.display = state.phase === 'shop' ? '' : 'none'
    if (state.phase !== 'shop') {
      panel.innerHTML = ''
      return
    }
    const shop = state.shop
    if (shop.path === null) {
      const itemAllowed = canChooseItemPath(state)
      panel.innerHTML = `
        <div class="shop-strip path-choice-strip">
          <div class="shop-ctrls"><div class="shop-title">Runde ${state.round}${shop.visit === 2 ? ' · 2. Shop' : ''}</div><span class="hint">EIN Pfad pro Besuch</span></div>
          <div class="path-cards">
            <button class="path-card" id="rPathChar"><b>🧙 Characters</b><span>5 Champions · Lock gratis · Bonus: +2 Free Item-Rerolls</span></button>
            <button class="path-card" id="rPathItem" ${itemAllowed ? '' : 'disabled'}><b>⚔️ Items</b><span>2 Waffen + 3 Passives + gratis Bonus-Champion${itemAllowed ? '' : '<br><b style="color:var(--teamB)">Kein Champion — wähle Character-Pfad.</b>'}</span></button>
          </div>
        </div>`
      $('rPathChar').addEventListener('click', () => {
        actions.choosePath('character')
        save()
        renderAll()
      })
      $('rPathItem').addEventListener('click', () => {
        if (actions.choosePath('item')) {
          save()
          renderAll()
        }
      })
      return
    }
    const cost = currentRerollCost(state)
    const free = shop.freeRerolls
    const slotCards = shop.slots
      .map((s, i) => {
        let title = ''
        let sub = ''
        if (s.kind === 'champion') {
          const c = cfg.champions[s.key]!
          title = c.name + (s.tier > 1 ? ` ★${s.tier}` : '')
          sub = `${c.role} · HP ${c.hp} · AD ${c.ad}${c.ap ? ` · AP ${c.ap}` : ''}`
        } else if (s.kind === 'weapon') {
          const w = cfg.weapons[s.key]!
          title = w.name + (s.tier > 1 ? ` ★${s.tier}` : '')
          sub = `${w.hands}H · ${w.base} dmg · R${w.range}${w.poisonOnHit ? ' · Gift' : ''}`
        } else {
          const p = cfg.passives[s.key]!
          title = p.name
          sub = `Team: +${p.pct}% ${p.stat} (sofort, permanent)`
        }
        const bought = s.bought
        const emoji = s.kind === 'champion' ? (SPECIES_EMOJI[s.key] ?? '🥔') : s.kind === 'weapon' ? '⚔️' : '✨'
        return `
        <div class="slot ${bought ? 'bought' : ''}" style="border-color:${s.locked ? 'var(--accent)' : RARITY_COLORS[s.rarity]}">
          <button data-lock="${i}" ${bought ? 'disabled' : ''} class="lockbtn ${s.locked ? 'active' : ''}" title="Lock ist gratis, überlebt Rerolls und hält bis zum nächsten Shop mit gleichem Pfad">${s.locked ? '🔒' : '🔓'}</button>
          <div class="slot-emoji">${emoji}</div>
          <b style="color:${RARITY_COLORS[s.rarity]}">${title}</b>
          <span class="hint">${sub}</span>
          <button class="buybtn" data-buy="${i}" ${bought || state.gold < s.price ? 'disabled' : ''}>${bought ? 'Gekauft' : `${s.price} 🪙`}</button>
        </div>`
      })
      .join('')
    const rerollLabel = free > 0 ? `Reroll (frei ×${free})` : `Reroll (${cost}🪙)`
    const ratioButtons = shop.path === 'item'
      ? `<button id="rRatioW" title="Ratio-Shift: +1 Waffe (gleicher Kostenzähler)">${free > 0 ? 'W+' : `W+ (${cost}🪙)`}</button>
         <button id="rRatioP" title="Ratio-Shift: +1 Passive (gleicher Kostenzähler)">${free > 0 ? 'P+' : `P+ (${cost}🪙)`}</button>`
      : ''
    const repair = state.fractures.length > 0
      ? `<button id="rRepair" ${shop.repairsUsed >= cfg.fracture.repairPerShop || state.gold < repairCost(state.round, cfg) ? 'disabled' : ''}
           title="Max 1 Reparatur pro Shop, älteste zuerst">🔧 Fracture reparieren (${repairCost(state.round, cfg)}🪙)</button>`
      : ''
    // S1 v1.2: Bonus-Charakter — gratis, fix pro Runde, lebt ausserhalb der Reroll-Slots
    let bonusCard = ''
    if (shop.path === 'item' && state.bonusChamp && state.bonusChamp.round === state.round) {
      const b = state.bonusChamp
      const champ = cfg.champions[b.key]!
      const bEmoji = SPECIES_EMOJI[b.key] ?? '🥔'
      bonusCard = b.taken
        ? `<div class="slot bonus bought"><div class="bonus-tag">Bonus</div><div class="slot-emoji">${bEmoji}</div><b>${champ.name}</b><span class="hint">Genommen ✓</span></div>`
        : `<div class="slot bonus" style="border-color:${RARITY_COLORS[champ.rarity]}" title="Nicht genommen = +1 Free Char-Reroll (2 Runden)"><div class="bonus-tag">Bonus · gratis</div>
             <div class="slot-emoji">${bEmoji}</div>
             <b style="color:${RARITY_COLORS[champ.rarity]}">${champ.name}</b>
             <span class="hint">${champ.role} · HP ${champ.hp} · AD ${champ.ad}${champ.ap ? ` · AP ${champ.ap}` : ''}</span>
             <button id="rTakeBonus" class="buybtn primary">Nehmen</button></div>`
    }
    panel.innerHTML = `
      <div class="shop-strip">
        <div class="shop-ctrls">
          <div class="shop-title">${shop.path === 'character' ? '🧙 Characters' : '⚔️ Items'} · R${state.round}${shop.visit === 2 ? ' · 2.' : ''}</div>
          <button id="rReroll" class="reroll-btn">${rerollLabel}</button>
          <div class="row">${ratioButtons}</div>
          ${repair}
        </div>
        <div class="slot-row">${bonusCard}${slotCards}</div>
      </div>
    `
    const takeBtn = document.getElementById('rTakeBonus')
    if (takeBtn) {
      takeBtn.addEventListener('click', () => {
        actions.takeBonusChamp()
        save()
        renderAll()
      })
    }
    $('rReroll').addEventListener('click', () => {
      actions.reroll('board')
      save()
      renderAll()
    })
    if (shop.path === 'item') {
      $('rRatioW').addEventListener('click', () => {
        actions.reroll('ratioWeapons')
        save()
        renderAll()
      })
      $('rRatioP').addEventListener('click', () => {
        actions.reroll('ratioPassives')
        save()
        renderAll()
      })
    }
    const repairBtn = document.getElementById('rRepair')
    if (repairBtn) {
      repairBtn.addEventListener('click', () => {
        actions.repairFracture()
        save()
        renderAll()
      })
    }
    panel.querySelectorAll<HTMLButtonElement>('[data-buy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        actions.buySlot(Number(btn.dataset.buy))
        save()
        renderAll()
      })
    })
    panel.querySelectorAll<HTMLButtonElement>('[data-lock]').forEach((btn) => {
      btn.addEventListener('click', () => {
        actions.toggleLock(Number(btn.dataset.lock))
        save()
        renderAll()
      })
    })
  }

  // ---------- Sidebar (Bench / Storage / Merges / Unit) ----------
  function champLabel(champKey: string): string {
    return state.config.champions[champKey]!.name
  }

  function renderSidebar(): void {
    const cfg = state.config
    const bench = benchChampions(state)
    $('rBenchCount').textContent = `${bench.length}/${cfg.bench.size}`
    // Feste Slot-Reihe (wie TFT): belegte + leere Slots bis zur Bench-Größe
    const benchEl = $('rBench')
    benchEl.innerHTML = ''
    for (let i = 0; i < cfg.bench.size; i++) {
      const c = bench[i]
      const slot = document.createElement('button')
      if (!c) {
        slot.className = 'bench-slot empty'
        slot.disabled = true
        benchEl.appendChild(slot)
        continue
      }
      slot.className = 'bench-slot' + (placingUid === c.uid ? ' picked' : '') + (selectedUid === c.uid ? ' selected' : '')
      slot.title = `${champLabel(c.champ)}${c.tier > 1 ? ` ★${c.tier}` : ''} — klick: aufstellen`
      slot.innerHTML = `
        <span class="bs-emoji">${SPECIES_EMOJI[c.champ] ?? '🥔'}</span>
        <span class="bs-name">${champLabel(c.champ)}</span>
        ${c.tier > 1 ? `<span class="bs-star">★${c.tier}</span>` : ''}`
      slot.addEventListener('click', () => {
        if (!canEdit()) return // kein Aufnehmen während Kampf/Ergebnis
        selectedUid = c.uid
        placingUid = c.uid
        renderAll()
      })
      benchEl.appendChild(slot)
    }
    const stored = storageWeapons(state)
    $('rStorageCount').textContent = `${stored.length}/${storageSize(cfg)}`
    $('rStorage').innerHTML = stored.length === 0 ? '<span class="hint">leer</span>' : ''
    for (const w of stored) {
      const div = document.createElement('button')
      div.className = 'item-chip'
      const wc = cfg.weapons[w.weapon]!
      div.innerHTML = `⚔️ ${wc.name}${w.tier > 1 ? ` ★${w.tier}` : ''}<span class="hint"> ${sellValue(w.buyPrice, w.boughtRound, state.round, cfg)}🪙</span>`
      div.title = 'Klick: verkaufen'
      div.addEventListener('click', () => {
        if (confirm(`${wc.name} für ${sellValue(w.buyPrice, w.boughtRound, state.round, cfg)} Gold verkaufen?`)) {
          actions.sellWeapon(w.uid)
          save()
          renderAll()
        }
      })
      $('rStorage').appendChild(div)
    }
    // merges
    const merges = findMerges(state)
    $('rMergeCard').style.display = merges.length > 0 ? '' : 'none'
    $('rMerges').innerHTML = ''
    for (const m of merges) {
      const btn = document.createElement('button')
      btn.className = 'merge-chip'
      const name = m.kind === 'champion' ? champLabel(m.key) : cfg.weapons[m.key]!.name
      btn.textContent = `⬆ 3× ${name} ★${m.tier}→★${m.tier + 1}`
      btn.addEventListener('click', () => {
        actions.executeMerge(m)
        save()
        renderAll()
      })
      $('rMerges').appendChild(btn)
    }
    renderUnitPanel()
  }

  function renderUnitPanel(): void {
    const cfg = state.config
    const card = $('rUnitCard')
    const c = selectedUid !== null ? championByUid(state, selectedUid) : undefined
    // Panel (mit Verkaufen/Bench/Ausrüsten) nur in der Aufstell-Phase — nie im Kampf (Bugfix)
    if (!c || !canEdit()) {
      card.style.display = 'none'
      return
    }
    card.style.display = ''
    const champ = cfg.champions[c.champ]!
    $('rUnitTitle').textContent = `${champ.name}${c.tier > 1 ? ` ★${c.tier}` : ''} ${c.hex ? '(Board)' : '(Bench)'}`
    const body = $('rUnitBody')
    const equipped = c.weaponUids
      .map((uid) => {
        const w = state.weapons.find((x) => x.uid === uid)
        if (!w) return ''
        return `<div class="row"><span>${cfg.weapons[w.weapon]!.name}${w.tier > 1 ? ` ★${w.tier}` : ''}</span><button data-unequip="${uid}">Ablegen</button></div>`
      })
      .join('')
    const stored = storageWeapons(state)
    const options = stored
      .map((w) => `<option value="${w.uid}">${cfg.weapons[w.weapon]!.name}${w.tier > 1 ? ` ★${w.tier}` : ''} (${cfg.weapons[w.weapon]!.hands}H)</option>`)
      .join('')
    body.innerHTML = `
      ${equipped || '<span class="hint">Keine Waffe (kämpft mit Faust)</span>'}
      ${stored.length > 0 ? `<div class="row"><select id="rEquipSel">${options}</select><button id="rEquipBtn">Ausrüsten</button></div>` : ''}
      <div class="row" style="margin-top:8px">
        ${c.hex ? '<button id="rToBench">Auf die Bench</button>' : ''}
        <button id="rSell">Verkaufen (${sellValue(c.buyPrice, c.boughtRound, state.round, cfg)}🪙)</button>
      </div>
    `
    body.querySelectorAll<HTMLButtonElement>('[data-unequip]').forEach((btn) => {
      btn.addEventListener('click', () => {
        actions.unequipWeapon(c.uid, Number(btn.dataset.unequip))
        save()
        renderAll()
      })
    })
    const equipBtn = document.getElementById('rEquipBtn')
    if (equipBtn) {
      equipBtn.addEventListener('click', () => {
        const sel = document.getElementById('rEquipSel') as HTMLSelectElement
        actions.equipWeapon(c.uid, Number(sel.value))
        save()
        renderAll()
      })
    }
    const toBench = document.getElementById('rToBench')
    if (toBench) {
      toBench.addEventListener('click', () => {
        actions.moveToBench(c.uid)
        save()
        renderAll()
      })
    }
    $('rSell').addEventListener('click', () => {
      if (confirm(`${champ.name} verkaufen?`)) {
        actions.sellChampion(c.uid)
        selectedUid = null
        save()
        renderAll()
      }
    })
  }

  // ---------- Overlays: Fokus-Pick / Ergebnis / Run-Ende ----------
  function renderOverlay(): void {
    const overlay = $('rOverlay')
    if (battle && !battle.finished) {
      overlay.classList.add('hidden')
      return
    }
    if (state.phase === 'focusPick') {
      const cards = (Object.keys(FOCUS_INFO) as FocusId[])
        .map((f) => `
          <button class="path-card" data-focus="${f}">
            <b>${FOCUS_INFO[f].icon} ${FOCUS_INFO[f].title}</b>
            <span>${FOCUS_INFO[f].desc}</span>
          </button>`)
        .join('')
      overlay.innerHTML = `<div class="box wide">
        <h2>PvE-Runde ${state.round} — Fokus wählen</h2>
        <p class="hint">„Draft your luck" — gilt bis zur nächsten PvE-Runde, dann Reset (docs/01)</p>
        <div class="path-cards focus-cards">${cards}</div>
      </div>`
      overlay.classList.remove('hidden')
      overlay.querySelectorAll<HTMLButtonElement>('[data-focus]').forEach((btn) => {
        btn.addEventListener('click', () => {
          actions.pickFocus(btn.dataset.focus as FocusId)
          save()
          renderAll()
        })
      })
      return
    }
    if (state.phase === 'result' && (battle === null || battle.finished)) {
      // battle === null: Resume mitten in der Result-Phase (Reload nach Kampf) —
      // lastCombat traegt alle Daten, das Playback ist verzichtbar (Review-Fix C8)
      const lc = state.lastCombat!
      const winLabel = lc.playerWon
        ? '<h2 style="color:var(--teamA)">Sieg!</h2>'
        : lc.autoLoss
          ? '<h2 style="color:var(--teamB)">Kampflos verloren</h2><p class="hint">Leeres Board — die Runde zählt als volle Niederlage (Rest-Power 100%).</p>'
          : '<h2 style="color:var(--teamB)">Niederlage</h2>'
      const hpRow = lc.playerWon
        ? '<tr><td>HP-Verlust</td><td>0</td></tr>'
        : `<tr><td>HP-Verlust</td><td>−${lc.hpLoss} <span class="hint">(StageBase ${lc.stageBase} + OutcomeMod ${lc.outcomeMod} bei ${(lc.enemyPowerBp / 100).toFixed(1)}% Rest-Power)</span></td></tr>`
      overlay.innerHTML = `<div class="box">
        ${winLabel}
        <table>
          <tr><td>Runde</td><td>${lc.round} · Versuch ${lc.attempt}</td></tr>
          ${hpRow}
          ${lc.fractureSpawned ? '<tr><td>Fracture</td><td style="color:#ff8c6e">+1 — 2 Losses in dieser Runde (blockt ein Backline-Feld)</td></tr>' : ''}
          <tr><td>Deine HP</td><td>${state.hp}/${state.config.hp.start}</td></tr>
        </table>
        <div class="row" style="justify-content:center">
          <button id="rContinue" class="primary">Weiter</button>
        </div>
      </div>`
      overlay.classList.remove('hidden')
      $('rContinue').addEventListener('click', () => {
        battle = null
        actions.continueAfterResult()
        save()
        selectedUid = null
        placingUid = null
        renderAll()
      })
      return
    }
    if (state.phase === 'ended') {
      overlay.innerHTML = `<div class="box">
        <h2>Run beendet</h2>
        <p style="font-size:28px;margin:8px 0">Stage <b>${state.round}</b> erreicht</p>
        <table>
          <tr><td>Deaths (Losses)</td><td>${state.stats.losses}</td></tr>
          <tr><td>Versuche</td><td>${state.stats.attempts}</td></tr>
          <tr><td>Clean Wins</td><td>${state.stats.cleanWins}</td></tr>
          <tr><td>Verbleibende HP</td><td>${state.hp}</td></tr>
        </table>
        <div class="row" style="justify-content:center">
          <button id="rNewRun" class="primary">Neuer Run</button>
          <button id="rToMenu">Hauptmenü</button>
        </div>
      </div>`
      overlay.classList.remove('hidden')
      $('rNewRun').addEventListener('click', () => {
        actions.abandonRun()
        opts.onExit()
      })
      $('rToMenu').addEventListener('click', () => {
        actions.abandonRun()
        opts.onExit()
      })
      return
    }
    overlay.classList.add('hidden')
  }

  // ---------- Kampf ----------
  function fight(): void {
    if (state.phase !== 'shop' || battle) return
    const out = actions.startFight() // schliesst den Shop ab (confirmShop) und kaempft
    if (!out) return
    if (out.result === null) {
      // S2-Bodennetz: kampfloser Auto-Loss (leeres Board) -> direkt zum Ergebnis
      battle = null
      renderAll()
      return
    }
    battle = { result: out.result, simTime: 0, playing: true, speed: 1, finished: false }
    renderer.prepare(out.result)
    $('rTickbar').classList.remove('hidden')
    renderAll()
  }

  // ---------- Canvas ----------
  function canvasPos(ev: MouseEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect()
    return { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
  }

  canvas.addEventListener('mousemove', (ev) => {
    if (battle) return
    const { x, y } = canvasPos(ev)
    hover = renderer.hexAt(x, y)
  })
  canvas.addEventListener('mouseleave', () => {
    hover = null
  })
  canvas.addEventListener('click', (ev) => {
    if (battle || state.phase !== 'shop') return
    const { x, y } = canvasPos(ev)
    const hex = renderer.hexAt(x, y)
    if (!hex) return
    const occupant = state.champions.find((c) => c.hex && c.hex.col === hex.col && c.hex.row === hex.row)
    if (placingUid !== null) {
      if (actions.placeChampion(placingUid, hex)) {
        selectedUid = placingUid
        placingUid = null
        save()
        renderAll()
        return
      }
    }
    if (occupant) {
      // erneuter Klick auf ausgewählte Unit: aufnehmen zum Versetzen
      if (selectedUid === occupant.uid) {
        placingUid = occupant.uid
      } else {
        selectedUid = occupant.uid
        placingUid = null
      }
      renderAll()
    }
  })
  canvas.addEventListener('contextmenu', (ev) => {
    ev.preventDefault()
    if (battle || state.phase !== 'shop') return
    const { x, y } = canvasPos(ev)
    const hex = renderer.hexAt(x, y)
    if (!hex) return
    const occupant = state.champions.find((c) => c.hex && c.hex.col === hex.col && c.hex.row === hex.row)
    if (occupant) {
      actions.moveToBench(occupant.uid)
      save()
      renderAll()
    }
  })

  // ---------- Buttons ----------
  $('rFight').addEventListener('click', fight)
  $('rUnitClose').addEventListener('click', () => {
    selectedUid = null
    placingUid = null
    renderAll()
  })
  $('rPause').addEventListener('click', () => {
    save() // Run pausiert — läuft im Hintergrund weiter (docs/10)
    opts.onExit()
  })
  $('rPlayPause').addEventListener('click', () => {
    if (battle) battle.playing = !battle.playing
    updateTickbar()
  })
  for (const s of [1, 2, 4]) {
    $(`rSpeed${s}`).addEventListener('click', () => {
      if (battle) battle.speed = s
      updateTickbar()
    })
  }
  $('rSkip').addEventListener('click', () => {
    if (battle) {
      battle.simTime = battle.result.endTick
      battle.playing = false
      battle.finished = true
      $('rTickbar').classList.add('hidden')
      renderAll()
    }
  })

  function updateTickbar(): void {
    if (!battle) return
    ;($('rPlayPause') as HTMLButtonElement).textContent = battle.playing ? '⏸' : '▶'
    for (const s of [1, 2, 4]) $(`rSpeed${s}`).classList.toggle('active', battle.speed === s)
  }

  // ---------- Re-Render ----------
  function renderAll(): void {
    // Board (Canvas) ist im TFT-Layout immer sichtbar — kein Umschalten mehr.
    renderer.resize()
    renderHud()
    renderShop()
    renderSidebar()
    renderOverlay()
  }

  // ---------- render loop ----------
  let lastFrame = performance.now()
  let lastCW = 0
  let lastCH = 0
  function frame(now: number): void {
    if (!running) return
    const dt = Math.min(0.1, (now - lastFrame) / 1000)
    lastFrame = now
    // Board-Bereich kann durch Bench/Shop-Höhe variieren -> bei Größenänderung neu vermessen
    if (canvas.clientWidth !== lastCW || canvas.clientHeight !== lastCH) {
      lastCW = canvas.clientWidth
      lastCH = canvas.clientHeight
      renderer.resize()
    }
    if (battle) {
      if (battle.playing) {
        battle.simTime += dt * state.config.combat.tickRate * battle.speed
        if (battle.simTime >= battle.result.endTick) {
          battle.simTime = battle.result.endTick
          battle.playing = false
          if (!battle.finished) {
            battle.finished = true
            $('rTickbar').classList.add('hidden')
            renderAll()
          }
        }
      }
      renderer.drawBattle(battle.simTime, now)
      const tick = Math.floor(battle.simTime)
      $('rTime').textContent = `Tick ${tick} / ${battle.result.endTick}`
    } else {
      const cfg = state.config
      const view: EditView = {
        unitsA: boardChampions(state).map((c) => ({
          col: c.hex!.col,
          row: c.hex!.row,
          champ: c.champ,
          role: cfg.champions[c.champ]!.role,
          tier: c.tier,
          weapons: c.weaponUids
            .map((uid) => state.weapons.find((w) => w.uid === uid)?.weapon)
            .filter((w): w is string => Boolean(w)),
        })),
        unitsB: [],
        hover,
        selected: selectedUid !== null ? championByUid(state, selectedUid)?.hex ?? null : null,
        brushRole: null,
        blocked: state.fractures.map((f) => ({ col: f.col, row: f.row })),
      }
      renderer.drawEdit(view)
    }
    requestAnimationFrame(frame)
  }

  // ---------- boot ----------
  renderAll()
  requestAnimationFrame(frame)
  const onResize = (): void => renderer.resize()
  window.addEventListener('resize', onResize)
  // STDB: Server hat den State ersetzt (Drift/Fremd-Tab) — UI-Zustand zuruecksetzen
  const unsubExternal = opts.subscribeExternal?.(() => {
    selectedUid = null
    placingUid = null
    battle = null
    $('rTickbar').classList.add('hidden')
    renderAll()
  })

  return () => {
    running = false
    unsubExternal?.()
    window.removeEventListener('resize', onResize)
  }
}

/** Hilfsfunktion fürs Hauptmenü: Kurzinfo eines gespeicherten Runs */
export function runSummary(state: RunState): string {
  return `Stage ${state.round} · ${state.hp} HP · ${state.gold} Gold`
}

export function isRecurringPveRound(state: RunState): boolean {
  return isRecurringPve(state.round, state.config)
}
